// src/components/ArticleEditor.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageContentWrapper, PageHeader } from '../main/LayoutStyles';
import { StyledButton, AlertBox } from '../main/CommonStyles';
import { FullArticleWrapper } from './KnowledgeStyles';
import apiClient from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Send, Loader2, AlertTriangle, ChevronLeft, Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Heading3 } from 'lucide-react';

// Importy dla Tiptap
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExtension from '@tiptap/extension-underline';

// --- Style dla nowego edytora i layoutu ---
const EditorLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr; /* Domyślnie jedna kolumna */
    gap: 2rem;
    width: 100%;

    @media (min-width: 1024px) {
        grid-template-columns: 1fr 1fr; /* Dwie kolumny na większych ekranach */
    }
`;

const FormPanel = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    background-color: #fff;
    padding: 1.5rem;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xs};

    label {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.textDark};
    }

    input, textarea {
        width: 100%;
        padding: ${({ theme }) => theme.spacing.sm};
        border: 1px solid ${({ theme }) => theme.colors.borderColor};
        border-radius: ${({ theme }) => theme.borderRadius};
        font-size: 0.95rem;

        &:focus {
            outline: none;
            border-color: ${({ theme }) => theme.colors.primary};
            box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLightest};
        }
    }
`;

const PreviewPanel = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 1.5rem;
    background-color: #fff;
    height: fit-content;
    position: sticky;
    top: 2rem;

    .article-content {
        h1, h2, h3 {
            margin-top: 0;
            line-height: 1.3;
        }
        p, ul, ol {
            margin-bottom: 1rem;
        }
        ul, ol {
            padding-left: 1.5rem;
        }
        strong {
            font-weight: 700;
        }
        em {
            font-style: italic;
        }
        u {
            text-decoration: underline;
        }
    }
`;

const PreviewPlaceholder = styled.div`
    text-align: center;
    color: ${({ theme }) => theme.colors.textMedium};
    height: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-style: italic;
`;

// Style dla paska narzędzi i edytora Tiptap
const TiptapToolbar = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    border-bottom: none;
    padding: 0.5rem;
    border-radius: 0.75rem 0.75rem 0 0;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    background-color: #f8fafc;

    button {
        background: none;
        border: none;
        padding: 0.25rem;
        border-radius: 4px;
        cursor: pointer;
        color: ${({ theme }) => theme.colors.textMedium};

        &:hover {
            background-color: ${({ theme }) => theme.colors.neutralMedium};
            color: ${({ theme }) => theme.colors.textDark};
        }
        &.is-active {
            background-color: ${({ theme }) => theme.colors.primaryLightest};
            color: ${({ theme }) => theme.colors.primaryDark};
        }
    }
`;

const TiptapEditorWrapper = styled.div`
    .ProseMirror {
        min-height: 250px;
        border: 1px solid ${({ theme }) => theme.colors.borderColor};
        border-radius: 0 0 0.75rem 0.75rem;
        padding: 0.75rem;
        font-size: 0.95rem;
        line-height: 1.6;

        &:focus {
            outline: none;
            border-color: ${({ theme }) => theme.colors.primary};
        }

        > * + * {
            margin-top: 0.75em;
        }

        ul, ol {
            padding: 0 1rem;
        }

        h1, h2, h3 {
            line-height: 1.2;
        }
    }
`;

// Komponent paska narzędzi dla Tiptap
const MenuBar = ({ editor }) => {
    if (!editor) {
        return null;
    }

    return (
        <TiptapToolbar>
            <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Pogrubienie"><Bold size={16}/></button>
            <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Kursywa"><Italic size={16}/></button>
            <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''} title="Podkreślenie"><Underline size={16}/></button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''} title="Nagłówek 1"><Heading1 size={16}/></button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''} title="Nagłówek 2"><Heading2 size={16}/></button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''} title="Nagłówek 3"><Heading3 size={16}/></button>
            <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''} title="Lista punktowana"><List size={16}/></button>
            <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''} title="Lista numerowana"><ListOrdered size={16}/></button>
        </TiptapToolbar>
    );
};


const ArticleEditor = () => {
    const navigate = useNavigate();
    const { isPsychologist } = useAuth();
    const [articleData, setArticleData] = useState({
        title: '',
        category: '',
        imageUrl: '',
        snippet: '',
    });
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3], },
            }),
            UnderlineExtension,
        ],
        content: content,
        onUpdate: ({ editor }) => {
            setContent(editor.getHTML());
        },
    });

    if (!isPsychologist) {
        return (
            <PageContentWrapper>
                <PageHeader>Brak dostępu</PageHeader>
                <AlertBox type="error"><p>Nie masz uprawnień do tworzenia artykułów.</p></AlertBox>
            </PageContentWrapper>
        );
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setArticleData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            const finalArticleData = { ...articleData, fullContent: content };
            await apiClient.post('/articles', finalArticleData);
            setSuccessMessage('Artykuł został pomyślnie wysłany do akceptacji!');
            setArticleData({ title: '', category: '', imageUrl: '', snippet: '' });
            setContent('');
            if (editor) editor.commands.clearContent();

            setTimeout(() => navigate('/app/knowledge'), 2000);
        } catch (err) {
            setError(err.response?.data?.message || 'Wystąpił błąd podczas wysyłania artykułu.');
        } finally {
            setLoading(false);
        }
    };

    const isPreviewEmpty = !articleData.title && !content;

    return (
        <PageContentWrapper className="custom-scrollbar">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <PageHeader>Edytor Artykułu</PageHeader>
                <StyledButton
                    $variant="outline"
                    onClick={() => navigate('/app/knowledge')}
                >
                    <ChevronLeft size={16} /> Wróć do Bazy Wiedzy
                </StyledButton>
            </div>

            {error && <AlertBox type="error" style={{ marginBottom: '1rem' }}><AlertTriangle />{error}</AlertBox>}
            {successMessage && <AlertBox type="success" style={{ marginBottom: '1rem' }}>{successMessage}</AlertBox>}

            <EditorLayout>
                <FormPanel onSubmit={handleSubmit}>
                    <FormField>
                        <label htmlFor="title">Tytuł</label>
                        <input id="title" name="title" value={articleData.title} onChange={handleInputChange} required />
                    </FormField>
                    <FormField>
                        <label htmlFor="category">Kategoria</label>
                        <input id="category" name="category" value={articleData.category} onChange={handleInputChange} required placeholder="np. Stres, Depresja" />
                    </FormField>
                    <FormField>
                        <label htmlFor="imageUrl">URL do obrazka (opcjonalnie)</label>
                        <input id="imageUrl" name="imageUrl" type="url" value={articleData.imageUrl} onChange={handleInputChange} placeholder="https://example.com/image.jpg" />
                    </FormField>
                    <FormField>
                        <label htmlFor="snippet">Krótki opis (do 500 znaków)</label>
                        <textarea id="snippet" name="snippet" value={articleData.snippet} onChange={handleInputChange} required maxLength="500" rows="4"></textarea>
                    </FormField>
                    <FormField>
                        <label>Pełna treść</label>
                        <TiptapEditorWrapper>
                            <MenuBar editor={editor} />
                            <EditorContent editor={editor} />
                        </TiptapEditorWrapper>
                    </FormField>
                    <StyledButton type="submit" disabled={loading} style={{ justifySelf: 'start', marginTop: '1rem' }}>
                        {loading ? <Loader2 className="icon-spin" /> : <Send />}
                        {loading ? 'Wysyłanie...' : 'Wyślij do akceptacji'}
                    </StyledButton>
                </FormPanel>

                <PreviewPanel>
                    <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0c4a6e' }}>Podgląd na żywo</h3>
                    {isPreviewEmpty ? (
                        <PreviewPlaceholder>Tutaj pojawi się podgląd artykułu...</PreviewPlaceholder>
                    ) : (
                        <FullArticleWrapper>
                            {articleData.imageUrl && <img src={articleData.imageUrl} alt="Podgląd obrazka" className="article-image-full" />}
                            <div
                                className="article-content"
                                dangerouslySetInnerHTML={{ __html: content || `<h1>${articleData.title}</h1><p>${articleData.snippet}</p>` }}
                            />
                        </FullArticleWrapper>
                    )}
                </PreviewPanel>
            </EditorLayout>
        </PageContentWrapper>
    );
};

export default ArticleEditor;